import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import LoginModal from '../LoginModal';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import womanChildImg from '../../assets/figma-extracts/woman-child-illustration_2025-06-15T15-54-20.png';

const Frame211: React.FC = () => {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const { session, clearSession } = useSessionPersistence();
  const goToDashboard = () => navigate('/frame-140');

  return (
    <div className="min-h-screen w-screen bg-gradient-to-b from-[#173a64] to-[#061c38] relative">
      {/* Background Circles - Responsive */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/4 w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 bg-blue-800 rounded-full opacity-10 transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute top-1/3 right-1/4 w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80 bg-blue-800 rounded-full opacity-8 transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-1/4 left-1/3 w-40 h-40 sm:w-48 sm:h-48 lg:w-64 lg:h-64 bg-blue-800 rounded-full opacity-12"></div>
        <div className="absolute bottom-1/3 right-1/3 w-44 h-44 sm:w-56 sm:h-56 lg:w-72 lg:h-72 bg-blue-800 rounded-full opacity-6"></div>
      </div>

      {/* Header Navigation - Responsive */}
      <div className="relative z-10 flex justify-between items-center px-4 sm:px-8 lg:px-12 py-2 sm:py-3 lg:py-4">
        {/* Logo - 2x bigger */}
          <BrandSymbol size="4xl" showText={true} showTagline={true} variant="light"/>

        {/* Desktop Navigation Menu */}
        <div className="hidden lg:flex items-center space-x-6">
          {session.accountId && (
            <button
              onClick={goToDashboard}
              className="text-[#65adc1] hover:text-white transition-colors text-sm"
            >
              Dashboard
            </button>
          )}
          <a
            href="https://www.endofanera.ai/resources"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#65adc1] hover:text-white transition-colors text-sm"
          >
            Resources
          </a>
          <a
            href="https://www.endofanera.ai/about"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#65adc1] hover:text-white transition-colors text-sm"
          >
            About
          </a>
          <div className="flex items-center space-x-4">
            {session.accountId ? (
              <button
                onClick={() => clearSession()}
                className="bg-[#173a64] border border-[#fffcf8] text-[#fffcf8] px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
              >
                Log out
              </button>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="bg-[#173a64] border border-[#fffcf8] text-[#fffcf8] px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
              >
                Log in →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu - Always Visible */}
      <div className="lg:hidden bg-[#173a64] border-b border-[#65adc1] px-4 py-3 relative z-20">
        <div className="flex flex-col space-y-3">
          <div className="flex justify-around items-center">
            {session.accountId && (
              <button
                onClick={goToDashboard}
                className="text-[#65adc1] hover:text-white transition-colors text-sm font-medium px-2 py-1"
              >
                Dashboard
              </button>
            )}
            <a
              href="https://www.endofanera.ai/resources"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#65adc1] hover:text-white transition-colors text-sm font-medium px-2 py-1"
            >
              Resources
            </a>
            <a
              href="https://www.endofanera.ai/about"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#65adc1] hover:text-white transition-colors text-sm font-medium px-2 py-1"
            >
              About
            </a>
          </div>
          <div className="flex justify-center">
            {session.accountId ? (
              <button
                onClick={() => clearSession()}
                className="bg-[#173a64] border border-[#fffcf8] text-[#fffcf8] px-6 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
              >
                Log out
              </button>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="bg-[#173a64] border border-[#fffcf8] text-[#fffcf8] px-6 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
              >
                Log in →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area - Full Screen */}
      <div className="relative z-10 flex justify-center items-center min-h-[60vh] py-8 px-2 sm:px-4">
        {/* Mother-child illustration - Responsive positioning */}
        <div className="absolute inset-0 flex justify-center items-center">
          <img
            src={womanChildImg}
            alt="Woman holding child illustration"
            className="w-auto h-[40vh] sm:h-[50vh] lg:h-[60vh] object-contain rounded-3xl"
          />
        </div>

        {/* Content Card - Responsive positioning and sizing */}
        <div className="relative z-20 w-full max-w-xs sm:max-w-sm lg:max-w-md bg-white rounded-lg shadow-2xl overflow-hidden mx-auto lg:transform lg:-translate-x-1/4">
          {/* Red Geometric Accents - Responsive sizing */}
          <div className="absolute top-0 left-0 w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-[#c53030] transform 
                          -translate-x-1 -translate-y-1 sm:-translate-x-2 sm:-translate-y-2 rotate-45 z-20"></div>
          <div className="absolute bottom-0 right-0 w-3 h-3 sm:w-5 sm:h-5 lg:w-6 lg:h-6 bg-[#c53030] transform 
                          translate-x-1 translate-y-1 rotate-45 z-20"></div>

          {/* Text Content - Responsive padding and typography */}
          <div className="p-4 sm:p-6 lg:p-8 flex flex-col justify-center bg-white relative z-30">
            <h1 className="text-base sm:text-lg lg:text-xl font-bold text-[#347644] mb-2 sm:mb-3 leading-tight">
              Build your legacy.<br />
              We'll handle the details.
            </h1>
            <p className="text-[#313121] text-xs sm:text-sm lg:text-base mb-3 sm:mb-4 leading-relaxed">
              Create an estate plan for yourself and your loved ones.
            </p>
            <button
              onClick={() => navigate('/frame-129')}
              className="bg-[#067681] text-white px-3 py-2 sm:px-4 sm:py-3 rounded hover:bg-teal-700
                         transition-colors font-medium text-xs sm:text-sm w-fit"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>

      {/* Footer - Responsive positioning */}
      <div className="absolute bottom-2 sm:bottom-4 lg:bottom-6 left-1/2 transform -translate-x-1/2 z-10">
        <p className="text-[#313121] text-xs opacity-60">©️2025 End of an Era, LLC</p>
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </div>
  );
};

export default Frame211;
